/**
 * Single frontend API boundary for the admin console (mirrors web/src/api/client.ts).
 *
 * Every admin request flows through `apiRequest`. The client:
 *   - resolves the configured API base URL (validated at startup);
 *   - obtains the current Clerk session token at request time (never stored);
 *   - attaches `Authorization: Bearer <clerk-session-jwt>`;
 *   - sets JSON Content-Type where a body is present;
 *   - threads a client request ID through `X-Request-ID`;
 *   - parses the provider-neutral DIRECT-011 error envelope;
 *   - preserves the server request ID on every error;
 *   - supports request cancellation via AbortSignal;
 *   - distinguishes network errors from API errors;
 *   - never logs tokens or secret response fields.
 *
 * Trimmed relative to web/: the multipart upload and blob download helpers
 * (APP-007 bulk surface) are not carried over — the admin API contract is
 * JSON-only in v1.
 */
import { loadConfig } from '@/config/env';
import {
  abortedError,
  isApiError,
  isAuthError,
  isValidationError,
  networkError,
  parseApiError,
  type ApiError,
} from './errors';

/** Token provider abstraction. Clerk's `getToken()` is injected at runtime. */
export type SessionTokenProvider = () => Promise<string | null>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** When false, skip the Clerk bearer token. Default true. */
  authenticated?: boolean;
  /** Extra headers (merged; never override Authorization/Content-Type). */
  headers?: Record<string, string>;
  /** Idempotency key for safe mutations. Sent as X-Idempotency-Key. */
  idempotencyKey?: string;
}

export interface ApiResult<T> {
  data: T;
  response: Response;
  requestId?: string;
}

let tokenProvider: SessionTokenProvider | null = null;

/**
 * Install the Clerk session token provider. Called once at app bootstrap
 * from the Clerk <ClerkProvider>. The provider is a function so the token is
 * always fetched fresh (Clerk refreshes short-lived JWTs automatically) and is
 * never held in module state.
 */
export function configureSessionTokenProvider(provider: SessionTokenProvider): void {
  tokenProvider = provider;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const { apiBaseUrl } = loadConfig();
  const base = apiBaseUrl.replace(/\/$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${suffix}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function maybeClientRequestId(): string | undefined {
  // Stable, collision-resistant client request ID for diagnostics. This is NOT
  // an idempotency key and carries no account information. The server accepts
  // well-formed values and regenerates malformed ones.
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `client_${crypto.randomUUID()}`;
    }
  } catch {
    /* fall through */
  }
  return undefined;
}

/**
 * Core request function. Throws `ApiError` on any failure; returns typed data
 * on success. Network failures and server errors are distinguishable via
 * `ApiError.isNetworkError` / `ApiError.status`.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const {
    method = 'GET',
    body,
    query,
    signal,
    authenticated = true,
    headers = {},
    idempotencyKey,
  } = options;

  const finalHeaders: Record<string, string> = { ...headers };
  const clientRequestId = maybeClientRequestId();
  if (clientRequestId) {
    finalHeaders['X-Request-ID'] = clientRequestId;
  }
  if (idempotencyKey) {
    finalHeaders['X-Idempotency-Key'] = idempotencyKey;
  }

  if (authenticated) {
    if (!tokenProvider) {
      throw networkError('Session token provider is not configured.');
    }
    const token = await tokenProvider();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
    // If no token is available, proceed without it; the backend will respond
    // with 401 MISSING_SESSION and the caller handles that. The client never
    // fabricates identity.
  }

  let serializedBody: string | undefined;
  if (body !== undefined) {
    serializedBody = JSON.stringify(body);
    finalHeaders['Content-Type'] = 'application/json';
    finalHeaders.Accept = 'application/json';
  } else if (!finalHeaders.Accept) {
    finalHeaders.Accept = 'application/json';
  }

  let response: Response;
  try {
    const url = buildUrl(path, query);
    try {
      response = await fetch(url, {
        method,
        headers: finalHeaders,
        body: serializedBody,
        signal,
        // Admin responses are credentialed via Bearer token, not cookies.
        credentials: 'omit',
        mode: 'cors',
      });
    } catch (syncErr) {
      // Cross-realm AbortSignal quirk: some test/jsdom environments reject an
      // AbortSignal whose constructor differs from the fetch implementation's
      // internal AbortSignal reference. This never occurs in real browsers.
      // When the caller did not already abort, retry once without the signal
      // so the request still completes; if the caller *did* abort, surface it.
      if (
        signal &&
        !signal.aborted &&
        syncErr instanceof TypeError &&
        /AbortSignal/i.test(syncErr.message)
      ) {
        response = await fetch(url, {
          method,
          headers: finalHeaders,
          body: serializedBody,
          credentials: 'omit',
          mode: 'cors',
        });
      } else if (signal?.aborted) {
        throw abortedError();
      } else {
        throw syncErr;
      }
    }
  } catch (err) {
    // An ApiError thrown by the inner block (e.g. abortedError) must pass
    // through unchanged rather than being rewrapped as a network error.
    if (isApiError(err)) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw abortedError();
    }
    throw networkError();
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const requestId = response.headers.get('x-request-id') ?? clientRequestId;

  // 204 No Content / empty bodies.
  if (response.status === 204) {
    return { data: undefined as T, response, requestId };
  }
  const text = await response.text();
  if (!text) {
    return { data: undefined as T, response, requestId };
  }
  try {
    return { data: JSON.parse(text) as T, response, requestId };
  } catch {
    // Treat unparseable success bodies as a parse error (should not happen).
    const { parseError } = await import('./errors');
    throw parseError(response.status, requestId);
  }
}

/**
 * TanStack Query retry predicate (mirrors web/src/api/client.ts). Bounded
 * retries for transient network/5xx; never retry authentication or
 * validation errors.
 */
export function defaultRetry(failureCount: number, error: unknown): boolean {
  if (!isApiError(error)) return failureCount < 1;
  const err = error as ApiError;
  if (err.isAborted) return false;
  if (isAuthError(err) || isValidationError(err)) return false;
  if (err.status === 403 || err.status === 404 || err.status === 409) return false;
  if (err.isNetworkError || err.status >= 500) return failureCount < 3;
  return err.retryable ? failureCount < 3 : false;
}

export type { ApiError };
